import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays } from "lucide-react";
import { format, differenceInBusinessDays, isToday, isTomorrow, startOfDay } from "date-fns";
import { isWithin12Hours } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { formatDateForAPI } from "@/lib/date-utils";

interface TaskDatePickerProps {
  task: any;
  onDateChange: (taskId: number, dueDate: string) => void;
}

export function TaskDatePicker({ task, onDateChange }: TaskDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  // API returns camelCase fields
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    task.dueDate && task.dueDate !== null ? new Date(task.dueDate) : undefined
  );

  // Update selectedDate when task.dueDate changes
  useEffect(() => {
    setSelectedDate(task.dueDate && task.dueDate !== null ? new Date(task.dueDate) : undefined);
  }, [task.dueDate]);

  const handleDateSelect = (date: Date | undefined) => {
    // Save current scroll position
    const scrollPosition = window.scrollY;
    
    setSelectedDate(date);
    if (date) {
      onDateChange(task.id, formatDateForAPI(date) || '');
    } else {
      onDateChange(task.id, '');
    }
    setIsOpen(false);
    
    // Restore scroll position after a small delay to account for DOM updates
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  };

  // Check if task was created within 12 hours
  const isRecent = task.dateAdded ? isWithin12Hours(new Date(task.dateAdded), new Date()) : false;
  
  const formatCreationDate = () => {
    if (!task.dateAdded) return '';
    const creationDate = new Date(task.dateAdded);
    if (isRecent || task.category === 'email_in' || task.category === 'email_out') {
      return format(creationDate, 'dd MMM HH:mm');
    }
    return format(creationDate, 'dd MMM');
  };

  // Calculate business days until due date
  const getDueDateInfo = () => {
    if (!selectedDate) return null;
    
    const today = startOfDay(new Date());
    const dueDate = startOfDay(selectedDate);
    
    // First check if it's actually today or tomorrow
    if (isToday(dueDate)) {
      return { text: "Today", isUrgent: true };
    } else if (isTomorrow(dueDate)) {
      return { text: "Tomorrow", isUrgent: true };
    } else {
      // Calculate actual days difference for display
      const actualDays = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      if (actualDays < 0) {
        return { text: `${Math.abs(actualDays)} days ago`, isUrgent: true };
      } else if (actualDays === 0) {
        // This shouldn't happen since we already checked isToday above
        return { text: "Today", isUrgent: true };
      } else if (actualDays <= 2) {
        // Show actual days for near future dates
        return { text: `in ${actualDays} days`, isUrgent: true };
      } else {
        // For dates further out, use business days
        const businessDays = differenceInBusinessDays(dueDate, today);
        return { text: `in ${businessDays} business days`, isUrgent: false };
      }
    }
  };

  const dueDateInfo = getDueDateInfo();
  // Check status dynamically - this will re-evaluate when task prop changes
  const isDone = task.status === "done" || task.status === "Done";

  return (
    <div className="text-xs space-y-1">
      {/* Creation date - dimmer */}
      <div className="text-gray-500">
        {formatCreationDate()}
      </div>
      
      {/* Due date - brighter, clickable */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "h-auto p-0 text-xs font-normal justify-start hover:bg-gray-700",
              !selectedDate && "text-gray-400 hover:text-gray-300",
              selectedDate && !isDone && !dueDateInfo?.isUrgent && "text-gray-300",
              selectedDate && !isDone && dueDateInfo?.isUrgent && "text-orange-400",
              selectedDate && isDone && "text-green-400"
            )}
          >
            {selectedDate ? (
              <>
                <span>Due: {format(selectedDate, 'dd MMM')}</span>
                {dueDateInfo && !isDone && (
                  <span className={cn(
                    "ml-1",
                    dueDateInfo.isUrgent && "text-orange-400",
                    !dueDateInfo.isUrgent && "text-gray-400"
                  )}>
                    ({dueDateInfo.text})
                  </span>
                )}
              </>
            ) : (
              <>
                <CalendarDays className="w-3 h-3 mr-1" />
                Set due date
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 navy-card border-gray-600" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            initialFocus
            className="bg-gray-800 text-white"
          />
          {selectedDate && (
            <div className="p-3 border-t border-gray-600 flex justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDateSelect(undefined)}
                className="text-xs text-gray-400 hover:text-gray-200"
              >
                Clear
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDateSelect(new Date())}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Today
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}